import { create } from "zustand";
import {
  getFiscalConfig,
  upsertFiscalConfig,
  uploadCertificate,
  uploadCaf,
  setPointOfSale,
  setEmissionRules,
  testFiscalConnection,
  declareCertification,
  activateFiscalConfig,
} from "../api/invoicing";
import i18n from "../i18n/index.js";

// Devuelve el mensaje legible del contrato de error tipado del backend
// ({ code, field, message }) o un fallback.
const msg = (err) =>
  err?.response?.data?.message ?? err?.message ?? i18n.t("Ocurrió un error.");

// Store del wizard de facturación. Mantiene el OperatorFiscalConfig actual
// (sin material sensible: el backend nunca lo devuelve) y expone una acción por
// paso. Cada acción persiste en el backend y refresca `config`.
const useInvoicingStore = create((set, get) => ({
  config: null, // OperatorFiscalConfig sanitizado, o null si aún no existe
  loading: false,
  saving: false,
  testing: false,
  error: null,

  reset: () => set({ config: null, error: null }),

  // Retoma (o detecta ausencia de) la configuración del operador.
  fetchConfig: async (country, environment) => {
    set({ loading: true, error: null });
    try {
      const { data } = await getFiscalConfig(country, environment);
      set({ config: data });
      return data;
    } catch (err) {
      if (err?.response?.status === 404) {
        set({ config: null });
        return null;
      }
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ loading: false });
    }
  },

  saveIdentity: async (dto) => {
    set({ saving: true, error: null });
    try {
      const { data } = await upsertFiscalConfig(dto);
      set({ config: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  uploadCertificate: async (file, password) => {
    const id = get().config?._id;
    set({ saving: true, error: null });
    try {
      const { data } = await uploadCertificate(id, file, password);
      set({ config: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  uploadCaf: async (file, docTypeCode) => {
    const id = get().config?._id;
    set({ saving: true, error: null });
    try {
      const { data } = await uploadCaf(id, file, docTypeCode);
      set({ config: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  savePointOfSale: async (pointOfSale) => {
    const id = get().config?._id;
    set({ saving: true, error: null });
    try {
      const { data } = await setPointOfSale(id, pointOfSale);
      set({ config: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  saveEmissionRules: async (dto) => {
    const id = get().config?._id;
    set({ saving: true, error: null });
    try {
      const { data } = await setEmissionRules(id, dto);
      set({ config: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  // Devuelve { ok, detail } y refresca config si la prueba marcó el gate.
  testConnection: async () => {
    const id = get().config?._id;
    set({ testing: true, error: null });
    try {
      const { data } = await testFiscalConnection(id);
      if (data?.ok) {
        // Re-leemos el config para reflejar connectionTested.
        const cfg = get().config;
        if (cfg) {
          await get().fetchConfig(cfg.country, cfg.environment);
        }
      }
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ testing: false });
    }
  },

  declareCertification: async (dto) => {
    const id = get().config?._id;
    set({ saving: true, error: null });
    try {
      const { data } = await declareCertification(id, dto);
      set({ config: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },

  activate: async () => {
    const id = get().config?._id;
    set({ saving: true, error: null });
    try {
      const { data } = await activateFiscalConfig(id);
      set({ config: data });
      return data;
    } catch (err) {
      set({ error: msg(err) });
      throw err;
    } finally {
      set({ saving: false });
    }
  },
}));

export default useInvoicingStore;
